/* eslint-disable indent */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import cn from 'classnames';
import Button, { UnstyledButton } from '../button';
import { EMPTY_ARRAY, isNullish, normalizeValue } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type {
  ComponentProps,
  TargetedEvent,
  TargetedFocusEvent,
  TargetedKeyboardEvent,
} from 'preact';
import type { default as FormField, Options, Option, OptGroup } from '../form-field';
import type { HTMLAttributes, Writeable } from '../../types/utility';

// @ts-expect-error TS2590 - union type too complex for declaration emit
type FormFieldProps = Omit<ComponentProps<typeof FormField>, 'isNative'>;
interface ComboboxProps {
  as: Exclude<FormFieldProps['as'], 'textarea'>;
  collapseOptions?: boolean;
  'data-button-type'?: 'button' | 'submit';
  options: Options;
  selectionType?: 'automatic' | 'manual';
  onInput?: (e: TargetedEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onChange?: (e: TargetedEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const OPTIONS_ABOVE = 'options-above';
const OPTIONS_ABOVE_INPUT = 'options-above-input';

function getSelectedOptions(
  options: Options,
  multiple: boolean,
  as: string,
  defaultValue?: string | string[],
  prevSelected?: Options,
) {
  const checkDefaultValue = !isNullish(defaultValue);
  const defaultValues = new Set(Array.isArray(defaultValue) ? defaultValue : [defaultValue]);

  const flatOptions = (options as Writeable<Options>).reduce((flatOptions, option) => {
    if (Array.isArray(option.options)) {
      flatOptions.push(...option.options);
    } else {
      flatOptions.push(option as Option);
    }

    return flatOptions;
  }, [] as Option[]);

  const selectedOptions = flatOptions.filter(({ value, selected }) => {
    if (checkDefaultValue) {
      return defaultValues.has(value);
    }

    /*
        generally for [type="search"] with dynamic option lists:
        option array references are unique, but the selected option can exist in multiple sets
          - searching `12` -> [12, 123, 1234, 12345] -> select 1234
          - searching `123` -> [123, 1234, 12345] -> no user selection, but 1234 _should_ stay selected
      */
    const wasPreviouslySelected = prevSelected?.some(
      (prev) => (prev as Exclude<Option, OptGroup>).value === value,
    );

    return selected || wasPreviouslySelected;
  });

  // set first option if <Select> and no default
  if (as === 'select' && flatOptions.length && !selectedOptions.length) {
    return [flatOptions[0] as Option];
  }

  // only return last element (but still as array) if not multiple
  if (!multiple) {
    selectedOptions.splice(0, selectedOptions.length - 1);
  }

  return selectedOptions;
}

function getDefaultValue({ isSelect, isInputSelect, multiple, selectedOptions, defaultValue }) {
  if (isSelect || (isInputSelect && !multiple)) {
    return selectedOptions[0]?.label || '';
  } else if (!Array.isArray(defaultValue)) {
    return defaultValue;
  }

  return undefined;
}

export default function Combobox({
  as,
  collapseOptions = false,
  'data-button-type': buttonType = 'button',
  options: passedOptions = [],
  // React 19 delivers `ref` as a regular prop; keep it out of the spread so
  // it can't clobber our internal ref
  ref: forwardedRef,
  selectionType = 'manual',
  onChange,
  onInput,
  ...fieldProps
}: ComboboxProps &
  FormFieldProps &
  Omit<HTMLAttributes<HTMLInputElement>, 'defaultValue'> & { defaultValue?: string | string[] }) {
  const { id, multiple } = fieldProps;
  const [filterValue, setFilterValue] = useState(() => {
    return as === 'input' && typeof fieldProps.defaultValue === 'string'
      ? normalizeValue(fieldProps.defaultValue)
      : '';
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [options, setOptions] = useState(passedOptions);
  const [selectedOptions, setSelectedOptions] = useState(() => {
    return getSelectedOptions(options, fieldProps.multiple, as, fieldProps.defaultValue);
  });
  const elementRef = useRef<HTMLInputElement>();
  const mergedElementRef = useMergedRef(elementRef, forwardedRef);
  const typeaheadRef = useRef('');
  const typeaheadClearTimeoutIdRef = useRef<number | undefined>(undefined);
  const activedescendantRef = useRef<HTMLAnchorElement | HTMLButtonElement>();
  const listRef = useRef<HTMLUListElement>();
  const hiddenRef = useRef<HTMLSelectElement>();
  const isSearchField = fieldProps.type === 'search';
  const hasRenderedRef = useRef(false);
  const previousActiveElementRef = useRef<EventTarget>();

  // check passed options for equality
  useEffect(() => {
    if (hasRenderedRef.current) {
      const areEqual =
        options === passedOptions ||
        (options.length === passedOptions.length &&
          options.every((option, index) => {
            return Object.entries(option).every(
              ([prop, value]) => value === passedOptions[index][prop],
            );
          }));

      if (!areEqual) {
        setOptions(passedOptions);
        setSelectedOptions((prevSelected) => {
          return getSelectedOptions(passedOptions, fieldProps.multiple, as, null, prevSelected);
        });
      }
    }
  }, [as, options, passedOptions, fieldProps.multiple]);

  // calc options position, if needed
  useEffect(() => {
    const { current: list } = listRef;
    const { current: input } = elementRef;

    if (isExpanded) {
      list.showPopover();

      // reset when expanded
      input.classList.remove(OPTIONS_ABOVE, OPTIONS_ABOVE_INPUT);
      list.style.maxHeight = '';

      const { innerHeight } = window;
      const isMultiple = input.dataset.multiple === 'true';
      const containerBuffer = 4; // px between top or bottom of input and list
      const viewportBuffer = '1rem'; // between viewport edges

      /*
        Note: CSS position-anchor isn't widely supported yet, so manually position
        See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/position-anchor)
      */
      const {
        left: inputLeft,
        bottom: inputBottom,
        top: inputTop,
        width: inputWidth,
      } = (isMultiple ? input.parentElement : input).getBoundingClientRect();

      list.style.inset = `${inputBottom + containerBuffer}px auto auto ${inputLeft}px`;
      list.style.width = `${inputWidth}px`;

      const { top: listTop, bottom: listBottom, height: listHeight } = list.getBoundingClientRect();
      const midway = innerHeight / 2;

      // check if bottom extends past window
      if (listBottom > innerHeight) {
        // check options height will fit in top portion
        if (listTop > midway) {
          list.style.insetBlock = `auto ${innerHeight - inputTop + containerBuffer}px`;

          // check new position and ensure top is visible
          const { top: listTopRepositioned } = list.getBoundingClientRect();
          if (listTopRepositioned < 0) {
            const maxHeight = listHeight - Math.abs(listTopRepositioned);
            const fallbackMaxHeight = 192;

            /*
              NOTE: generally for <MultiSelect> with so many options selected the container top is no longer in the viewport,
              so force height around 4 options (~192px) and position relative to the input, not the container
            */
            if (maxHeight < fallbackMaxHeight) {
              const { top: actualInputTop } = input.getBoundingClientRect();
              list.style.insetBlock = `auto ${innerHeight - actualInputTop + containerBuffer}px`;
              list.style.maxHeight = `${fallbackMaxHeight}px`;
              return;
            }

            list.style.maxHeight = `calc(${maxHeight}px - ${viewportBuffer})`;
          }
        } else {
          list.style.maxHeight = `calc(${innerHeight - listTop}px - ${viewportBuffer})`;
        }
      }
    } else {
      list.hidePopover();
    }

    return () => {
      list?.hidePopover();
    };
  }, [isExpanded, selectedOptions]);

  // scroll activedescendantRef into view
  useEffect(() => {
    activedescendantRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [focusIndex]);

  const handleSelectValues = useCallback((e: CustomEvent<{ values: string[] }>) => {
    const selectedValues = e.detail.values;
    const options = Array.from(
      (listRef.current?.querySelectorAll('.option') as NodeListOf<HTMLButtonElement>) ??
        EMPTY_ARRAY,
    ).filter(({ value }) => selectedValues.includes(value));

    setSelectedOptions((prev) => {
      const currentValues = new Set(prev.map(({ value }) => value));
      const newOptions = options
        .filter(({ value }) => !currentValues.has(value))
        .map(({ textContent: label, value, id, dataset }) => ({
          label,
          value,
          id,
          ...dataset,
        }));

      return [...prev, ...newOptions];
    });
  }, []);

  // trigger onChange event for changed values for select and multi-select
  useEffect(() => {
    const hiddenSelect = hiddenRef.current;
    const input = elementRef.current;

    if (onChange) {
      hiddenSelect?.addEventListener('change', onChange as EventListener);
    }

    input?.addEventListener('setvalues', handleSelectValues as EventListener);

    return () => {
      hiddenSelect?.removeEventListener('change', onChange as EventListener);
      input?.removeEventListener('setvalues', handleSelectValues as EventListener);
    };
  }, [onChange, handleSelectValues]);

  // dispatch change event
  useEffect(() => {
    if (hasRenderedRef.current) {
      hiddenRef.current?.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: {
            input: elementRef.current,
            value: hiddenRef.current.multiple
              ? Array.from(hiddenRef.current.selectedOptions).map(({ value }) => value)
              : hiddenRef.current.value,
          },
        }),
      );
    } else {
      hasRenderedRef.current = true;
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (!collapseOptions) {
      return;
    }

    const container = elementRef.current?.closest('.has-collapse-options') as HTMLElement;
    if (!container) {
      return;
    }

    const formField = container.parentElement;

    // height _should_ stay static
    const { height } = container.getBoundingClientRect();
    formField.style.setProperty('--combobox-height', `${height}px`);

    const observer = new ResizeObserver(() => {
      const { width } = container.getBoundingClientRect();
      formField.style.setProperty('--combobox-width', `${width}px`);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [collapseOptions]);

  const resetTypeaheadTimeout = useCallback(() => {
    if (typeaheadClearTimeoutIdRef) {
      clearTimeout(typeaheadClearTimeoutIdRef.current);
    }
    typeaheadClearTimeoutIdRef.current = window.setTimeout(() => {
      typeaheadRef.current = '';
      typeaheadClearTimeoutIdRef.current = undefined;
    }, 1000);
  }, []);

  const handleKeyDown = useCallback(
    (e: TargetedKeyboardEvent<HTMLInputElement>) => {
      const { currentTarget } = e;
      const isExpanded = currentTarget.getAttribute('aria-expanded') === 'true';
      const isSelect = currentTarget.dataset.as === 'select';
      const childOptions = listRef.current?.querySelectorAll('.option') ?? [];

      switch (e.code) {
        case 'Enter':
          e.preventDefault();
          activedescendantRef.current?.click();
          return;
        case 'Space':
          if (!isSelect) {
            return;
          }

          e.preventDefault();

          if (!typeaheadRef.current) {
            if (isExpanded) {
              activedescendantRef.current?.click();
            } else {
              setIsExpanded(true);
            }
          }

          return;
        case 'Escape':
          if (isExpanded) {
            // prevent esc from closing open dialog
            e.preventDefault();

            setIsExpanded(false);

            if (!isSelect) {
              setFocusIndex(-1);
            }
          }
          return;
        case 'ArrowLeft':
        case 'ArrowRight':
          return;
        case 'ArrowUp': {
          e.preventDefault();

          if (!isExpanded) {
            setIsExpanded(true);
            return;
          }

          let prevIndex = focusIndex - 1;
          let i = 0;

          do {
            if (prevIndex < 0) {
              prevIndex = childOptions.length - 1;
            }

            const option = childOptions[prevIndex];

            if (option?.disabled || option?.closest('fieldset[disabled]')) {
              prevIndex--;
            } else {
              break;
            }
          } while (prevIndex > -1 && ++i < childOptions.length);

          setFocusIndex(prevIndex);

          return;
        }
        case 'ArrowDown': {
          e.preventDefault();
          if (!isExpanded) {
            setIsExpanded(true);
            return;
          }

          let nextIndex = focusIndex + 1;
          let i = 0;

          do {
            if (nextIndex >= childOptions.length) {
              nextIndex = 0;
            }

            const option = childOptions[nextIndex];

            if (option?.disabled || option?.closest('fieldset[disabled]')) {
              nextIndex++;
            } else {
              break;
            }
          } while (nextIndex <= childOptions.length && ++i < childOptions.length);

          setFocusIndex(nextIndex);

          return;
        }
        case 'Tab':
          if (isExpanded && isSelect) {
            e.preventDefault();
            return;
          }
          break;
        case 'Backspace':
        case 'Delete':
          if (isSelect) {
            e.preventDefault();
          }
          break;
        default:
          if (isSelect) {
            e.preventDefault();
            resetTypeaheadTimeout();

            const newTypeahead = normalizeValue(typeaheadRef.current + e.key);
            typeaheadRef.current = newTypeahead;

            const whiteSpace = /\s/g;
            const match = Array.from(childOptions).findIndex(({ disabled, textContent: label }) => {
              // the switch...case for 'Space' prevents typehead from tracking spaces, so ensure we remove when comparing
              return (
                !disabled && normalizeValue(label.replace(whiteSpace, '')).startsWith(newTypeahead)
              );
            });

            if (match > -1) {
              if (isExpanded) {
                setFocusIndex(match);
              } else {
                const { textContent: label, value, id, dataset } = childOptions[match];
                elementRef.current.value = label;
                setSelectedOptions([{ label, value, id, ...dataset }]);
              }
            }
          }
          return;
      }
    },
    [focusIndex, resetTypeaheadTimeout],
  );

  // Mouse and keyboard share one highlight pointer (DATA-2326): moving the
  // mouse over an option claims focusIndex, so the keyboard-pinned option
  // (automatic mode pins index 0 on every keystroke) can't read as a second
  // "hovered" row. mousemove (not mouseover/mouseenter) so re-renders under a
  // resting cursor don't re-fire and fight ArrowUp/ArrowDown navigation.
  const handleOptionMouseMove = useCallback(
    (e: TargetedEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
      const { currentTarget } = e;
      // anchors have no `disabled`; mirror the keyboard-navigation skip rules
      if (
        (currentTarget as HTMLButtonElement).disabled ||
        currentTarget.closest('fieldset[disabled]')
      ) {
        return;
      }

      const index = Number(currentTarget.dataset.index);
      if (!Number.isNaN(index)) {
        setFocusIndex((prev) => (prev === index ? prev : index));
      }
    },
    [],
  );

  const handleSelect = useCallback(
    (e: TargetedEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (!elementRef.current) {
        return;
      }

      const { currentTarget } = e;

      // allow default action
      if (currentTarget instanceof HTMLAnchorElement) {
        setIsExpanded(false);
        return;
      }

      e.stopPropagation();

      const { current } = elementRef;
      const { value, id } = currentTarget;
      const label = currentTarget.dataset.label ?? '';
      const index = currentTarget.dataset.index ?? -1;
      const option = { label, value, id, ...currentTarget.dataset };

      if (current.dataset.as === 'select') {
        setSelectedOptions([option]);
        setFocusIndex(+index);
        current.value = label;
      } else if (current.dataset.multiple === 'true') {
        const isAlreadySelected = selectedOptions.some((opt) => opt.value === value);
        if (isAlreadySelected) {
          setSelectedOptions(selectedOptions.filter((opt) => opt.value !== value));
        } else {
          setSelectedOptions([...selectedOptions, option]);
        }
        setFilterValue('');
        setFocusIndex(-1);
        current.value = '';
      } else if (current) {
        setFilterValue(normalizeValue(value));
        setFocusIndex(-1);

        if (current.dataset.isInputSelect) {
          current.value = label;
          setSelectedOptions([option]);
        } else {
          current.value = value || label || '';
          current.dispatchEvent(new CustomEvent('input', { bubbles: true }));
        }
      }

      // only set focus on user-initiated events
      if (e.isTrusted) {
        current.focus();
      }

      // ensure list stays open for multiple selects
      if (current.dataset.multiple !== 'true') {
        setIsExpanded(false);
      }
    },
    [selectedOptions],
  );

  const handleUnselect = useCallback(
    (e: TargetedEvent<HTMLButtonElement, MouseEvent>) => {
      setSelectedOptions(
        selectedOptions.filter((selectedOption) => selectedOption.value !== e.currentTarget.value),
      );

      // set focus back to input if it was focused before unselect
      if (previousActiveElementRef.current === elementRef.current) {
        elementRef.current?.focus();
      }

      // reset input when clear button clicked
      if (e.currentTarget.name === 'clear') {
        elementRef.current.value = '';
        setFilterValue('');
      }
    },
    [selectedOptions],
  );

  const handleUnselectFocus = useCallback((e: TargetedFocusEvent<HTMLElement>) => {
    previousActiveElementRef.current = e.relatedTarget;
  }, []);

  const handleClearAll = useCallback((e: TargetedEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedOptions([]);
    setFilterValue('');
    if (elementRef.current) {
      elementRef.current.value = '';
    }
  }, []);

  // for collapseOptions to determine if input should be re-focused
  const shouldReFocusRef = useRef(false);

  /* select only */
  const handleClick = useCallback(
    (e: TargetedEvent<HTMLElement, MouseEvent | PointerEvent>) => {
      if (elementRef.current?.dataset.as === 'select' && !elementRef.current.readOnly) {
        setIsExpanded(!isExpanded);
      }

      /*
        NOTE: for collapseOptions:
        - pointerdown fires before blur
        - check if clicking within options container to maintain focus so options can be removed
        - clicking outside will lose focus and re-collapse options
      */
      if (e.type === 'pointerdown') {
        const { clientX, clientY } = e;
        const { left, top, right, bottom } = listRef.current.parentElement.getBoundingClientRect();
        const isWithinOptions =
          clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
        shouldReFocusRef.current = isWithinOptions;
      }
    },
    [isExpanded],
  );

  /* datalist only */
  const handleInput = useCallback(
    async (e: TargetedEvent<HTMLInputElement>) => {
      const { currentTarget } = e;
      if (currentTarget.dataset.as !== 'select') {
        const isExpanded = currentTarget.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) {
          setIsExpanded(true);
        }

        setFocusIndex(selectionType === 'automatic' ? 0 : -1);

        if (!isSearchField) {
          setFilterValue(normalizeValue(currentTarget.value));
        } else {
          // clear button clicked
          if (!(e instanceof InputEvent)) {
            setSelectedOptions([]);
          }

          onInput?.(e);
        }
      }
    },
    [onInput, selectionType, isSearchField],
  );

  const handleFocus = useCallback((e: TargetedFocusEvent<HTMLInputElement>) => {
    if (
      e.currentTarget.dataset.as !== 'select' &&
      e.currentTarget.getAttribute('aria-expanded') !== 'true'
    ) {
      setIsExpanded(true);
    }

    // on initial focus allow all options to be visible
    if (e.currentTarget.dataset.isInputSelect === 'true') {
      setFilterValue('');
    }
  }, []);

  const handleBlur = useCallback(
    (e: TargetedFocusEvent<HTMLInputElement>) => {
      const { currentTarget, relatedTarget } = e;
      if (
        currentTarget.dataset.as !== 'select' &&
        currentTarget.getAttribute('aria-expanded') === 'true' &&
        (!listRef.current?.contains(relatedTarget as Node) || listRef.current === relatedTarget)
      ) {
        setIsExpanded(false);
        setFocusIndex(-1);

        if (shouldReFocusRef.current) {
          elementRef.current?.focus();
          shouldReFocusRef.current = false;
        }
      }

      // ensure single-select input keeps originally selected label
      if (
        currentTarget.dataset.isInputSelect &&
        currentTarget.dataset.multiple !== 'true' &&
        selectedOptions.length
      ) {
        const label = selectedOptions[0]?.label as string;
        if (label && currentTarget.value !== label) {
          currentTarget.value = label;
        }
      }
    },
    [selectedOptions],
  );

  const handleCut = useCallback((e) => {
    if (e.currentTarget.dataset.as === 'select') {
      e.preventDefault();
    }
  }, []);

  const handleClickCollapsedOptions = useCallback(() => {
    const { current } = elementRef;
    if (current?.getAttribute('aria-expanded') !== 'true') {
      elementRef.current?.focus();
    }
  }, []);

  const activedescendant = `${fieldProps.name}-option-${focusIndex}`;
  const isSelect = as === 'select';
  const listId = `list-${id}`;
  const isInputSelect = fieldProps['data-is-input-select'];
  const needsHiddenField = isSelect || multiple || isInputSelect;

  // reset and assign iterative index to each <Option>
  const optionIndexRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  optionIndexRef.current = 0;

  const Option = useCallback(
    ({ href, icon, iconColor, value, label, disabled, subtext, target, ...props }: Option) => {
      const index = optionIndexRef.current;
      optionIndexRef.current += 1;

      const Element = typeof href === 'string' ? 'a' : 'button';
      const descriptionId = `${fieldProps.name}-option-subtext-${index}`;
      const id = `${fieldProps.name}-option-${index}`;

      return (
        <li key={value || label} role="presentation">
          <Element
            /* @ts-ignore-error Type 'MutableRef<HTMLAnchorElement | HTMLButtonElement>' provides no match for the signature '(instance: HTMLButtonElement): void' */
            ref={focusIndex === index ? activedescendantRef : undefined}
            aria-describedby={descriptionId}
            aria-selected={selectedOptions.some((opt) => opt.value === (value ?? label))}
            className="option"
            data-icon={icon}
            data-icon-color={iconColor ? `var(--${iconColor})` : undefined}
            data-index={index}
            data-focused={focusIndex === index}
            data-label={label}
            disabled={disabled}
            href={href}
            id={id}
            role="option"
            tabIndex={-1}
            target={target}
            type={buttonType}
            value={value}
            onClick={handleSelect}
            onMouseMove={handleOptionMouseMove}
            style={{ '--icon-color': iconColor ? `var(--${iconColor})` : undefined }}
            {...props}
          >
            {label}
            {subtext && (
              <small aria-hidden="true" id={descriptionId}>
                {subtext}
              </small>
            )}
          </Element>
        </li>
      );
    },
    [
      fieldProps.name,
      buttonType,
      focusIndex,
      handleSelect,
      handleOptionMouseMove,
      selectedOptions,
    ],
  );

  const OptGroup = useCallback(
    ({ label, disabled = false, options }: OptGroup) => {
      return options.length ? (
        <li role="presentation">
          <fieldset disabled={disabled}>
            <legend>{label}</legend>
            <ul>
              {options.map((option, index) => (
                <Option
                  key={option.value || (option.label as string) || index.toString()}
                  {...option}
                />
              ))}
            </ul>
          </fieldset>
        </li>
      ) : null;
    },
    [Option],
  );

  const filteredOptions = options
    .map((option) => {
      // create shallow clone of optgroup options
      return Array.isArray(option.options) ? { ...option, options: [...option.options] } : option;
    })
    .filter((option) => {
      // remove options that don't match input text
      if (!isSelect && !isSearchField) {
        if (Array.isArray(option.options)) {
          option.options = option.options.filter((option) => filterOptions(option, filterValue));
          return option.options.length;
        }

        return filterOptions(option, filterValue);
      }

      return true;
    });

  /*
    show a temporary option for [type="search"] w/o affecting currently selected options
  */
  if (fieldProps['data-pending-option']) {
    filteredOptions.length = 0;
    filteredOptions.push({
      label: fieldProps['data-pending-option'],
      value: '',
      disabled: true,
    });
  }

  return (
    <div
      className={cn('combobox-container', {
        'has-multiple': multiple,
        'has-collapse-options': collapseOptions,
      })}
      data-expanded={isExpanded}
      data-icon={!multiple ? selectedOptions?.[0]?.icon : undefined}
      data-options-length={filteredOptions.length}
      onClickCapture={collapseOptions ? handleClickCollapsedOptions : undefined}
      style={!multiple ? { '--icon-color': selectedOptions?.[0]?.iconColor } : undefined}
    >
      {multiple &&
        selectedOptions.map((selected, index) => (
          <p
            key={selected.value}
            className="selected-option"
            data-icon={selected.icon}
            style={{
              '--icon-color': selected.iconColor ? `var(--${selected.iconColor})` : undefined,
            }}
          >
            {collapseOptions && index === 0 ? <span>{selected.label}</span> : selected.label}
            <Button
              hierarchy="tertiary"
              size="small"
              icon="close"
              aria-label="Remove"
              isIconOnly
              name="removeOption"
              value={selected.value}
              onClick={handleUnselect}
              onFocus={handleUnselectFocus}
            />
          </p>
        ))}

      {!isExpanded && collapseOptions && selectedOptions.length > 1 && (
        <p className="selected-option count">+{selectedOptions.length - 1}</p>
      )}

      <input
        type="text"
        {...fieldProps}
        ref={mergedElementRef}
        aria-activedescendant={isExpanded ? activedescendant : undefined}
        aria-autocomplete={!isSelect ? 'list' : undefined}
        aria-controls={listId}
        aria-expanded={isExpanded}
        autoComplete="off"
        data-as={as}
        data-multiple={multiple}
        data-options-length={filteredOptions.length}
        list={undefined}
        maxLength={isSelect ? 0 : undefined}
        name={needsHiddenField ? undefined : fieldProps.name}
        data-name={needsHiddenField ? fieldProps.name : undefined}
        required={!needsHiddenField ? fieldProps.required : undefined}
        role="combobox"
        defaultValue={getDefaultValue({
          isSelect,
          isInputSelect,
          multiple,
          selectedOptions,
          defaultValue: fieldProps.defaultValue,
        })}
        onBlur={handleBlur}
        onClick={handleClick}
        onInput={handleInput}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onCut={handleCut}
      />

      {!isSearchField && isInputSelect && !multiple && selectedOptions.length > 0 && (
        <UnstyledButton
          className="combobox-clear"
          icon="close"
          isIconOnly
          name="clear"
          value={selectedOptions[0].value}
          aria-label={`Clear ${selectedOptions[0].label}`}
          onClick={handleUnselect}
        />
      )}

      {multiple && selectedOptions.length > 0 && (
        <UnstyledButton
          className="combobox-clear-all"
          icon="close"
          isIconOnly
          name="clearAll"
          aria-label="Clear all selections"
          onPointerDown={handleClearAll}
        />
      )}

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <ul
        ref={listRef}
        className="options"
        id={listId}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="listbox"
        onClick={handleClick}
        onPointerDownCapture={collapseOptions ? handleClick : undefined}
        popover="manual"
        aria-multiselectable={multiple}
      >
        {filteredOptions.map((option) => {
          return Array.isArray(option.options) ? (
            <OptGroup {...(option as OptGroup)} />
          ) : (
            <Option {...(option as Option)} />
          );
        })}
      </ul>

      {/* keep track of the selected values to pass for use in FormData and/or onChange handlers */}
      {needsHiddenField && (
        <select
          hidden
          name={fieldProps.name}
          value={selectedOptions[0]?.value || ''}
          required={fieldProps.required}
          ref={hiddenRef}
          multiple={multiple}
          data-id={id}
        >
          {selectedOptions.map(({ label, value, ...props }) => (
            <option key={value || label} value={value} {...props} selected>
              {label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function filterOptions(option, filterValue) {
  const { value, label, subtext } = option;
  return [value ?? '', label, typeof subtext === 'string' ? subtext : ''].some((str) =>
    normalizeValue(str).includes(filterValue),
  );
}

const isNodeList = (el) => el.nodeName !== 'SELECT' && typeof el[Symbol.iterator] === 'function';

// helpers for getting and setting <Combobox> values in unit tests
export function setComboBoxSelectedIndex(
  element: HTMLElement | NodeListOf<HTMLButtonElement>,
  index: number,
) {
  const option = isNodeList(element)
    ? element[index]
    : ((element as HTMLElement).parentElement?.querySelector(
        `.options > :nth-child(${index + 1}) > button[role="option"]`,
      ) as HTMLButtonElement);
  option?.click();
}

export function setComboBoxSelectedValue(
  element: HTMLElement | NodeListOf<HTMLButtonElement>,
  value: string,
) {
  const option = isNodeList(element)
    ? Array.from(element as NodeListOf<HTMLButtonElement>).find((node) => node.value === value)
    : ((element as HTMLElement).parentElement?.querySelector(
        `.options button[role="option"][value="${value}"]`,
      ) as HTMLButtonElement);
  option?.click();
}

export function setComboBoxSelectedValues(element: HTMLInputElement, values: string[]) {
  element.dispatchEvent(new CustomEvent('setvalues', { detail: { values } }));
}

export function getComboBox(key: string | HTMLElement, container?: Element) {
  let element;

  if (typeof key === 'string') {
    element = (container ?? document).querySelector(`[data-name="${key}"]`) as HTMLInputElement;
  } else {
    element = key;
  }

  if (!element) {
    return;
  }

  const { parentElement } = element;

  return {
    input: parentElement?.querySelector('input[role="combobox"][data-name]') as HTMLInputElement,
    select: element.parentElement?.querySelector(
      `select[name="${element.dataset.name}"]`,
    ) as HTMLSelectElement,
    options: element.parentElement?.querySelectorAll(
      `.options button[role="option"]`,
    ) as NodeListOf<HTMLButtonElement>,
  };
}
